import { type InputHTMLAttributes, useId } from 'react';

import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import styles from './SearchInput.module.css';

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'value'
> {
  value: string;
  onChange: (value: string) => void;
  /** Read by screen readers; the placeholder is not a label. */
  label: string;
  /** Shows a spinner in place of the search icon — the debounce is waiting, or the list is fetching. */
  loading?: boolean;
}

/** A search box for toolbars. Debounce the value in the caller before querying. */
export function SearchInput({
  value,
  onChange,
  label,
  loading = false,
  className,
  id,
  ...inputProps
}: SearchInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className={cn(styles.wrap, className)}>
      <label htmlFor={inputId} className={styles.hiddenLabel}>
        {label}
      </label>
      {loading ? (
        <Spinner size="sm" label="Searching" className={styles.icon} />
      ) : (
        <Icon name="search" size={18} className={styles.icon} />
      )}
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={styles.input}
        autoComplete="off"
        {...inputProps}
      />
      {value !== '' && (
        <button
          type="button"
          className={styles.clear}
          aria-label="Clear search"
          onClick={() => {
            onChange('');
          }}
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}
